import { useRef, useState } from 'react';

import { Listbox } from '@headlessui/react';
import { Label } from '@iwoca/orion';
import cn from 'classnames';
import { useField } from 'formik';

import styles from './MultipleChoiceDropdown.module.css';
import { useOnClickOutside } from '../../../../components/NavBars/NavBarBase/Menu/useClickOutside';
import DownwardTriangleSVG from '../../../../components/svg/DownwardsArrow.svg';
import CheckmarkSVG from '../../../../components/svg/GreenCheckmark.svg';
import WhiteCrossSVG from '../../../../Seller/Payments/assets/whiteCross.svg';

type MultipleChoiceDropdownOption = {
  title: string;
  id: string;
};
type MultipleChoiceDropdownOptions = MultipleChoiceDropdownOption[];

export const MultipleChoiceDropdown = ({
  name,
  labelText,
  subtitleText,
  fieldData,
  placeholder,
  disabled,
}: {
  name: string;
  labelText?: string;
  subtitleText?: string;
  fieldData: MultipleChoiceDropdownOptions;
  placeholder?: string;
  disabled?: boolean;
}) => {
  const [field, { touched, error }, { setValue: setCurrentSelections }] =
    useField(name);
  const [status, setStatus] = useState({ open: false });
  const containerReference = useRef<HTMLDivElement>(null);

  useOnClickOutside(containerReference!, () => {
    setStatus({ open: false });
  });

  const unToggleOption = async ({ id }: { id: string }) => {
    const mutableOptions = field.value;
    mutableOptions.splice(mutableOptions.indexOf(id), 1);
    await setCurrentSelections(mutableOptions);
  };

  const currentOptions = field.value.map((id: string) =>
    fieldData.find((el) => el.id === id),
  );

  return (
    <div ref={containerReference}>
      {labelText && (
        <Label className={styles.title} htmlFor={name}>
          {labelText}
        </Label>
      )}
      {subtitleText && <p className={styles.subtitleText}>{subtitleText}</p>}
      <Listbox
        value={currentOptions}
        onChange={async (values) => {
          await setCurrentSelections(
            (values ?? [])
              .map((valueOption: MultipleChoiceDropdownOption) =>
                fieldData.find(
                  (field: MultipleChoiceDropdownOption) =>
                    field.id === valueOption.id,
                ),
              )
              .map((option: MultipleChoiceDropdownOption) => option.id),
          );
        }}
        multiple
        disabled={disabled}
      >
        <Listbox.Button
          className={cn(styles.mainField, {
            [styles.disabled]: disabled,
            [styles.mainFieldWithPips]: currentOptions.length > 0,
          })}
          onClick={(e) => {
            // Check if the title is there
            // @ts-expect-error
            if (e.target?.title === 'remove') {
              return;
            }
            setStatus({ open: !status.open });
          }}
        >
          <img
            src={DownwardTriangleSVG}
            className={styles.downwardTriangle}
            width="13px"
            alt=""
          />
          {(field.value ?? []).length === 0 && (
            <p className={styles.placeholder}>{placeholder}</p>
          )}
          {currentOptions.map((option: MultipleChoiceDropdownOption) => (
            <button
              key={option.id}
              type="button"
              className={styles.selectedOptionPip}
              title="remove"
              onClick={() => unToggleOption({ id: option.id })}
            >
              {option.title}
              <img className={styles.pipCross} src={WhiteCrossSVG} alt="" />
            </button>
          ))}
        </Listbox.Button>
        {status.open && (
          <Listbox.Options className={styles.choicesBox} static>
            {fieldData.map((option) => (
              <Listbox.Option
                key={option.id}
                value={option}
                className={cn(styles.choicesRow)}
              >
                <p className={styles.choicesRowText}>{option.title}</p>
                {(field.value ?? []).includes(option.id) && (
                  <img src={CheckmarkSVG} alt="" className={styles.checkmark} />
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        )}
      </Listbox>
      {touched && error && <p className={styles.errorStyle}>{error}</p>}
    </div>
  );
};
